// Rich marketing content for a festival's public page, keyed by venue slug so
// it's environment-agnostic (staging + prod share the slug). Hardcoded for now
//, Madruga is the flagship; this can move to venue/event config + admin later.
// Purely content/structure: styling comes from the venue theme tokens, so real
// brand assets (palette, fonts, imagery) drop in without touching this file.

export type FestivalFeature = { icon?: string; title: string; body?: string };
export type FestivalFaq = { q: string; a: string };
export type FestivalTravel = { mode: string; detail: string };

export type FestivalHero = {
  /** Big background image URL. Bundled asset (/venues/<slug>/…) or images-bucket URL. */
  image?: string;
  /** Optional background video (mp4/webm). Autoplays muted+looped; `image`/`poster` is the fallback. */
  video?: string;
  /** Video poster frame (defaults to `image`). */
  poster?: string;
  /** Wordmark/typeface logo overlaid on the hero (SVG preferred, else PNG). */
  logo?: string;
  logoAlt?: string;
};

export type FestivalStoryContent = {
  /** When set, the root renders a minimal centred splash instead of the full
   *  marketing page. `mode` picks what the splash does:
   *   - "signup" (default): logo + presale mailing-list email capture.
   *   - "buy": logo + a "Buy tickets" CTA that fades into the presale ticket
   *     purchase flow (tickets first, accommodation revealed once a ticket is
   *     added). */
  splash?: {
    mode?: "signup" | "buy";
    ctaLabel?: string;
    dates?: string;
    heading?: string;
    subline?: string;
    /** Which mailing LIST (MailingList id) the sign-up form feeds into. Lets the
     *  admin point the presale page at a chosen list (e.g. "Early Bird")
     *  without losing others, and swap back anytime. Unset → the venue's
     *  default list. */
    listId?: string;
    /** On-sale (buy-mode) copy. The top-level heading/subline/ctaLabel are the
     *  SIGN-UP copy; when the splash flips to `mode: "buy"` it uses these
     *  instead so "Sign up for the presale" becomes e.g. "Tickets on sale now"
     *  without re-editing. Each field falls back to the sign-up value if unset.
     *  `dates` stays shared (same festival dates in both states). */
    buy?: {
      heading?: string;
      subline?: string;
      ctaLabel?: string;
      /** Subtitle under the "Tickets" step on the buy page (e.g. "Secure your
       *  Early Bird pass"). Blank → a neutral default. Editable so organisers
       *  aren't stuck with hardcoded copy. */
      ticketsSubtitle?: string;
      /** On-sale overrides, each falls back to the default (sign-up) value /
       *  the top-level backdrops when unset, so flipping modes never blanks
       *  anything that hasn't been customised. */
      dates?: string;
      backdrop?: string;
      backdropPortrait?: string;
    };
  };
  /** Accent + display-font override (until the venue theme carries the real
   *  brand palette/typeface). `displayFont` is a full CSS font-family stack.
   *  `fontUrl` is an optional uploaded font file (woff2/otf/ttf) injected via
   *  @font-face as "FestivalDisplay", set displayFont to use it. */
  theme?: { accent?: string; accentFg?: string; displayFont?: string; fontUrl?: string };
  /** Optional full-page background image behind all sections (else a themed gradient). */
  backdrop?: string;
  /** Optional portrait/tall crop of the backdrop, served on narrow (mobile)
   *  screens via <picture>, the landscape `backdrop` center-crops badly on a
   *  tall phone, so a dedicated portrait image fills the frame properly. */
  backdropPortrait?: string;
  /** Big hero background (image or video) + logo overlay. */
  hero?: FestivalHero;
  /** One-line strapline under the hero title. */
  strapline?: string;
  /** Longer concept paragraph(s). */
  intro?: string;
  /** "What to expect" grouped feature cards. */
  expect?: { heading: string; features: FestivalFeature[] }[];
  /** Lineup names (flat, a–z or curated). */
  lineup?: { heading?: string; headliners?: string[]; artists?: string[]; note?: string };
  /** Getting-there travel rows. */
  travel?: FestivalTravel[];
  /** Practical info / FAQ. */
  faq?: FestivalFaq[];
  /** Optional gallery image URLs (filled once brand assets arrive). */
  gallery?: string[];
  /** Outbound-email sender identity for this festival's confirmations + presale
   *  broadcasts. `fromName` + `replyTo` apply immediately (mail still sends from
   *  the verified platform address, just branded with this name). `fromEmail` is
   *  used as the actual From ONLY once `domainVerified` is true, that flag is
   *  platform-set from a live SES check, never editable by the venue, so a
   *  mistyped/unverified address can never break or bounce a send. */
  sender?: {
    fromName?: string;
    fromEmail?: string;
    replyTo?: string;
    /** Server-authoritative: set from a live SES domain-verification check. */
    domainVerified?: boolean;
  };
};

const MADRUGA: FestivalStoryContent = {
  // Presale mailing-list capture: Madruga logo + email signup ("we'll email you
  // the moment presale tickets go live"). Flips to "buy" (the ticket-selling
  // splash) on sale day, the admin toggles it in /admin/festival, which writes
  // appConfig.festival.splash.mode and overrides this seed default per venue.
  splash: {
    mode: "signup",
    ctaLabel: "Sign up",
    heading: "Sign up for 2027 presale",
    subline: "Limited tickets go live this Friday at 10am",
  },
  // ── Brand assets (fill once exported from the .ai files) ──────────────────
  // Drop the logo at web/public/venues/madruga/ (export "Madruga Logo Final.ai"
  // → icon.svg; the typeface logo → wordmark.svg). Big hero image/video: upload
  // to the images bucket (or web/public/venues/madruga/hero.jpg if small) and
  // set the URL(s) here. Left undefined for now → graceful fallback to the
  // event image + title text.
  // Madruga brand amber-orange, sampled from the live site's Tickets button
  // (#E4A843), brighter/more illuminated than the wordmark gold. On-accent
  // text is a deep brand brown (not white) for contrast on the light-ish amber.
  theme: { accent: "#E4A843", accentFg: "#1a1206", displayFont: '"Alte Haas Grotesk", system-ui, sans-serif' },
  // Wide crowd-at-the-mansion shot behind the whole landing/splash (WebP,
  // ~300KB vs ~740KB JPEG, universally supported). Portrait staircase shot for
  // mobile so the backdrop fills a tall phone instead of hard-cropping.
  backdrop: "/venues/madruga/hero.webp",
  backdropPortrait: "/venues/madruga/hero-portrait.webp",
  hero: {
    image: "/venues/madruga/hero.webp",
    // video: "https://<cdn>/images/madruga/hero.mp4",
    logo: "/venues/madruga/wordmark-white.png", // exported from the .ai/PNG, inverted to white for the dark hero
    logoAlt: "Madruga Festival",
  },
  strapline: "An intimate mansion party meets festival, deep in the Welsh hills.",
  intro:
    "Madruga is a weekend for people who come to dance. World-class music across intimate " +
    "rooms in and around a mansion, wrapped in wellness, proper food and the Black Mountains. " +
    "No massive fields, just you, a like-minded crowd, and the party running till sunrise.",
  expect: [
    {
      heading: "Wellness",
      features: [
        { icon: "🧖", title: "Outdoor sauna & indoor sauna" },
        { icon: "♨️", title: "Hot tubs & cold plunges" },
        { icon: "🔥", title: "Fire pit & deck chairs" },
        { icon: "💆", title: "Massages" },
        { icon: "🏊", title: "Indoor pool (weekend ticket)" },
      ],
    },
    {
      heading: "Food & drink",
      features: [
        { icon: "🍕", title: "NY pizza", body: "By the slice or whole pies" },
        { icon: "🥙", title: "Kebab shop" },
        { icon: "🍗", title: "Fried chicken, burgers & fries" },
        { icon: "☕", title: "Madrugada café", body: "Breakfast & coffee" },
        { icon: "🍺", title: "A British pub", body: "Inside the mansion, draught, cocktails, snacks" },
      ],
    },
    {
      heading: "Beyond the music",
      features: [
        { icon: "🧘", title: "Yoga & sound baths" },
        { icon: "💿", title: "Record shop" },
        { icon: "🎤", title: "Karaoke" },
        { icon: "🎱", title: "Drag bingo & speed dating" },
      ],
    },
  ],
  lineup: {
    heading: "Previous lineups",
    headliners: ["joy orbison"],
    artists: [
      "anna wall", "anthea", "dr banana", "dj adhd", "papa nugs", "shy one", "Dave Harvey",
      "darwin", "hutch", "KT", "unai Trotti", "Manami", "DMX Krew", "Kassian", "Aloka",
    ],
    note: "2026 lineup dropping soon.",
  },
  travel: [
    { mode: "By car", detail: "Parking £25 for the weekend, carpool where you can. Live-in vehicles £40 (no hookups)." },
    { mode: "By train", detail: "Hereford station is 35 min away; hourly shuttle on Friday + return Monday, £30/trip." },
    { mode: "Times", detail: "London ~3h · Bristol ~1h45 by train · Manchester ~2h15 by train." },
  ],
  faq: [
    { q: "Where is it?", a: "Baskerville Hall, Clyro (Hay-on-Wye), Wales, surrounded by the Black Mountains, overlooking the Brecon Beacons, a stone's throw from the river Wye." },
    { q: "When?", a: "Friday 24 – Monday 27 July. On-site from 12:00 Friday; off-site by noon Monday. Party till 5am Fri, 6am Sat, 3am Sun." },
    { q: "Age", a: "Strictly 21+, bring valid photo ID matching your ticket name." },
    { q: "Drinks", a: "Only bar-bought alcohol on the mansion grounds. You may bring max 12 cans + 1 wine bag. No spirits, no glass." },
    { q: "Safety", a: "On-site wellness team and medics. Zero tolerance for drugs, and for harassment, abuse or violence, immediate eviction." },
    { q: "Tickets", a: "Non-refundable. Name must match photo ID; name changes £20 by email. Resale only via Tixel." },
  ],
  gallery: [],
};

// Hardcoded seeds, used as the default until a venue has admin-editable
// content saved on appConfig.festival.
const BY_SLUG: Record<string, FestivalStoryContent> = { madruga: MADRUGA };

export function getFestivalContent(slug: string | undefined | null): FestivalStoryContent | null {
  if (!slug) return null;
  return BY_SLUG[slug] ?? null;
}

// Resolve the content for a venue: admin-editable appConfig.festival wins, then
// the hardcoded seed for the venue slug. Loosely typed to avoid a types.ts cycle.
export function resolveFestivalContent(
  venue: { slug?: string; appConfig?: { festival?: FestivalStoryContent } | null } | null | undefined,
): FestivalStoryContent | null {
  return venue?.appConfig?.festival ?? getFestivalContent(venue?.slug) ?? null;
}
